<template>
  <Dropdown
    ref="dropdown"
    :close-on-click-within="true"
    menu-max-height="20rem"
  >
    <template #triggerContent>
      <Button
        :label="label"
        icon="plus"
        severity="secondary"
        outlined
        size="sm"
        :disabled="disabled"
      />
    </template>

    <DropdownItemGroup
      v-for="group in groupedPlaceholders"
      :key="group.category"
      :text="group.category"
    >
      <DropdownItem
        v-for="placeholder in group.placeholders"
        :key="placeholder.token"
        @item-clicked="handlePlaceholderSelected(placeholder.token)"
      >
        <span class="font-medium">{{ placeholder.label }}</span>
      </DropdownItem>
    </DropdownItemGroup>
  </Dropdown>
</template>

<script setup lang="ts">
// vue
import { computed, ref } from 'vue'

// components
import Button from '@/components/button/Button.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import DropdownItemGroup from '@/components/dropdown/DropdownItemGroup.vue'

// types
import type { CommunicationTemplatePlaceholderOption } from '@/utilities/communications/templatePlaceholders'

/**
 * Placeholder group rendered inside the dropdown.
 */
interface PlaceholderGroup {
  category: string
  placeholders: CommunicationTemplatePlaceholderOption[]
}

const props = defineProps({
  label: {
    type: String,
    default: 'Insert Placeholder'
  },
  placeholders: {
    type: Array as () => CommunicationTemplatePlaceholderOption[],
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  select: [token: string]
}>()

const dropdown = ref<InstanceType<typeof Dropdown> | null>(null)

/**
 * Groups placeholders by category to keep the dropdown scannable.
 */
const groupedPlaceholders = computed<PlaceholderGroup[]>(() => {
  return props.placeholders.reduce<PlaceholderGroup[]>((groups, placeholder) => {
    const group = groups.find(item => item.category === placeholder.category)

    // Create a new group the first time a category appears.
    if (!group) {
      groups.push({
        category: placeholder.category,
        placeholders: [placeholder]
      })
      return groups
    }

    group.placeholders.push(placeholder)
    return groups
  }, [])
})

/**
 * Emits a selected placeholder token when the picker is interactive.
 */
function handlePlaceholderSelected(token: string): void {
  // Disabled pickers should not mutate the parent editor.
  if (props.disabled) {
    return
  }

  emit('select', token)
}

/**
 * Opens the picker menu programmatically, used by editor slash commands.
 */
function open(): void {
  // Do not open disabled pickers from exposed component methods.
  if (props.disabled) {
    return
  }

  dropdown.value?.open()
}

defineExpose({
  open
})
</script>
